import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../helpers/api';
import { deleteMedia, uploadMedia } from '../helpers/upload';

const PartnerAdEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitDisabled, setSubmitDisabled] = useState(false);

    // Predefined color options
    const colorOptions = [
        { value: '#fff0f7', label: 'Rose pâle', bg: '#fff0f7' },
        { value: '#f0f8ff', label: 'Bleu ciel', bg: '#f0f8ff' },
        { value: '#f0fff4', label: 'Vert menthe', bg: '#f0fff4' },
        { value: '#fff8e6', label: 'Crème', bg: '#fff8e6' }
    ];

    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        url: '',
        backgroundColor: colorOptions[0].value,
        isActive: false,
        image: null
    });

    const [previewImage, setPreviewImage] = useState(null);
    const [imageFile, setImageFile] = useState(null);

    useEffect(() => {
        if (id !== 'new') {
            fetchPartnerAd();
        } else {
            setIsLoading(false);
        }
    }, [id]);

    const fetchPartnerAd = async () => {
        try {
            const response = await api.get(`/partner-ads/${id}?populate=*`);
            const data = response.data.data;
            
            setFormData({
                title: data.title || '',
                subtitle: data.subtitle || '',
                url: data.url || '',
                backgroundColor: data.backgroundColor || colorOptions[0].value,
                isActive: data.isActive || false,
                image: data.image || null
            });

            if (data.image) {
                setPreviewImage(API_BASE_URL + data.image.url);
            }
        } catch (err) {
            console.error('Erreur lors du chargement de la publicité:', err);
            setError('Erreur lors du chargement de la publicité');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitDisabled(true);

        try {
            // Handle image upload/update if there's a new image
            let imageData = formData.image;
            
            if (imageFile) {
                if (formData.image) {
                    // Delete previous image
                    await deleteMedia(formData.image.id);
                }
                
                // Upload new image
                imageData = await uploadMedia(imageFile);
            }

            const dataToSubmit = {
                title: formData.title,
                subtitle: formData.subtitle,
                url: formData.url,
                backgroundColor: formData.backgroundColor,
                isActive: formData.isActive,
                image: imageData ? { id: imageData.id } : null
            };

            if (id === 'new') {
                // Create new partner ad
                await api.post('/partner-ads', {
                    data: dataToSubmit
                });
            } else {
                // Update existing partner ad
                await api.put(`/partner-ads/${id}`, {
                    data: dataToSubmit
                });
            }

            navigate('/partner-ads', {
                state: {
                    message: 'Publicité partenaire ' + (id === 'new' ? 'créée' : 'modifiée') + ' avec succès'
                }
            });
        } catch (err) {
            console.error('Erreur lors de la sauvegarde:', err);
            setError('Erreur lors de la sauvegarde');
            setSubmitDisabled(false);
        }
    };

    const handleImageDelete = async () => {
        if (formData.image && formData.image.id) {
            try {
                await deleteMedia(formData.image.id);
                setFormData(prev => ({
                    ...prev,
                    image: null
                }));
                setPreviewImage(null);
                setImageFile(null);
            } catch (err) {
                console.error('Erreur lors de la suppression de l\'image:', err);
                setError('Erreur lors de la suppression de l\'image');
            }
        } else {
            // Just clear the preview
            setPreviewImage(null);
            setImageFile(null);
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid px-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                    {id === 'new' ? 'Nouvelle publicité partenaire' : 'Modifier la publicité partenaire'}
                </h2>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/partner-ads')}
                >
                    Retour à la liste
                </button>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-8">
                                <div className="mb-3">
                                    <label className="form-label">Titre</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Sous-titre</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="subtitle"
                                        value={formData.subtitle}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">URL</label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Couleur d'arrière-plan</label>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {colorOptions.map(color => (
                                            <div 
                                                key={color.value} 
                                                className="form-check"
                                                style={{ minWidth: '120px' }}
                                            >
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="backgroundColor"
                                                    id={`color-${color.value.substring(1)}`}
                                                    value={color.value}
                                                    checked={formData.backgroundColor === color.value}
                                                    onChange={handleChange}
                                                />
                                                <label 
                                                    className="form-check-label d-flex align-items-center" 
                                                    htmlFor={`color-${color.value.substring(1)}`}
                                                >
                                                    <span 
                                                        className="rounded me-2"
                                                        style={{ 
                                                            backgroundColor: color.bg,
                                                            display: 'inline-block',
                                                            width: '20px',
                                                            height: '20px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    ></span>
                                                    {color.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                        Code hexadécimal: {formData.backgroundColor}
                                    </small>
                                </div>

                                <div className="form-check mb-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="isActive"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label" htmlFor="isActive">
                                        Publicité active
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="mb-3">
                                    <label className="form-label">Image</label>
                                    <div className="card p-3 mb-3">
                                        <div className="text-center mb-3">
                                            {previewImage ? (
                                                <div className="position-relative">
                                                    <img
                                                        src={previewImage}
                                                        alt="Aperçu"
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '200px' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                                                        onClick={handleImageDelete}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div 
                                                    className="d-flex align-items-center justify-content-center bg-light rounded" 
                                                    style={{ height: '150px' }}
                                                >
                                                    <span className="text-muted">Aucune image</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="imageUpload" className="form-label">
                                                Choisir une image
                                            </label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                id="imageUpload"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                            />
                                        </div>
                                        <div className="alert alert-info mb-0">
                                            <small>
                                                <i className="bi bi-info-circle me-2"></i>
                                                <strong>Format recommandé:</strong> Ratio 3:2<br />
                                                <span className="ms-4">Exemples:</span>
                                                <ul className="mb-0 mt-1">
                                                    <li>480×320</li>
                                                    <li>600×400</li>
                                                    <li>720×480</li>
                                                    <li>900×600</li>
                                                </ul>
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="mb-4">
                            <h5>Aperçu</h5>
                            <div 
                                className="card p-3" 
                                style={{ 
                                    backgroundColor: formData.backgroundColor,
                                    maxWidth: '500px'
                                }}
                            >
                                <div className="row g-3 align-items-center">
                                    {(previewImage || formData.image) && (
                                        <div className="col-md-4">
                                            <img
                                                src={previewImage || (formData.image ? API_BASE_URL + formData.image.url : '')}
                                                alt="Aperçu"
                                                className="img-fluid rounded"
                                            />
                                        </div>
                                    )}
                                    <div className={`col-md-${(previewImage || formData.image) ? '8' : '12'}`}>
                                        <h5 className="mb-1">{formData.title || 'Titre de la publicité'}</h5>
                                        <p className="mb-2 small">{formData.subtitle || 'Sous-titre de la publicité'}</p>
                                        <div className="small text-primary text-decoration-underline">
                                            {formData.url || 'https://exemple.com'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate('/partner-ads')}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={submitDisabled}
                            >
                                {submitDisabled ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Enregistrement...
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PartnerAdEdit;